import { type RefObject, useEffect } from "react";

/**
 * Giving focus back to a gallery's link when the screen it lives on is drawn again.
 *
 * The gallery window sits on the search list. Opened from favourites, history or downloads, closing it goes
 * back to that screen, which mounts afresh and loads its list: the link the user came from does not exist at
 * the moment the window closes. So the window leaves a request here (the gallery it showed last, then the
 * one it was opened on), and the link of the best of those takes focus when it mounts. The request is
 * short-lived and ends at the user's next key press or pointer press, so it never pulls focus away from
 * something the user has moved on to.
 */
const VALID_MS = 10_000;

interface Pending {
  /** candidates, best first */
  gids: readonly number[];
  until: number;
  /** index in `gids` of the link that has focus so far; `gids.length` = none yet */
  taken: number;
  focused: HTMLElement | null;
}

let pending: Pending | null = null;

export function cancelFocusReturn(): void {
  pending = null;
  document.removeEventListener("keydown", cancelFocusReturn, true);
  document.removeEventListener("pointerdown", cancelFocusReturn, true);
}

export function requestFocusReturn(gids: readonly number[]): void {
  cancelFocusReturn();
  pending = { gids, until: Date.now() + VALID_MS, taken: gids.length, focused: null };
  document.addEventListener("keydown", cancelFocusReturn, true);
  document.addEventListener("pointerdown", cancelFocusReturn, true);
}

/** For the link of gallery `gid`: takes focus on mount when the gallery window asked for it. */
export function useFocusReturn(gid: number, ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const request = pending;
    if (request === null) return;
    if (Date.now() > request.until) {
      cancelFocusReturn();
      return;
    }
    const rank = request.gids.indexOf(gid);
    if (rank < 0 || rank >= request.taken || !ref.current) return;
    const active = document.activeElement;
    // only when focus is nowhere (the page itself) or on a link this same request focused a moment ago
    if (active !== null && active !== document.body && active !== request.focused) return;
    ref.current.focus();
    request.taken = rank;
    request.focused = ref.current;
  }, [gid, ref]);
}
